import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { useFilteredList } from "@/hooks/useFilteredList";
import { isSuperAdmin } from "@/types";
import type { AuthUser, User } from "@/types";
import { UserCard } from "./user/UserCard";
import { AddUserDialog } from "./user/AddUserDialog";

export function UserManager({ currentUser }: { currentUser: AuthUser }) {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterRole, setFilterRole] = useState<string>("all");

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: queryKeys.users,
    queryFn: () => api.listUsers(),
  });
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: queryKeys.roles,
    queryFn: () => api.listRoles(),
  });

  const users = usersData?.users ?? [];
  const roles = rolesData?.roles ?? [];
  const loading = usersLoading || rolesLoading;

  const currentIdx = roles.findIndex((r) => r.name === currentUser.role);
  const assignable = currentIdx >= 0 ? roles.slice(currentIdx + 1) : [];

  const visibleUsers = users.filter((u) => {
    if (isSuperAdmin(u) && !isSuperAdmin(currentUser)) return false;
    return u.id === currentUser.id || assignable.some((r) => r.name === u.role);
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.users });
    queryClient.invalidateQueries({ queryKey: queryKeys.roles });
  };

  const userFilter = useCallback(
    (u: User, q: string) => {
      const matchesSearch = !q ||
        u.username.toLowerCase().includes(q) ||
        (u.role_label ?? u.role).toLowerCase().includes(q);
      const matchesRole = filterRole === "all" || u.role === filterRole;
      return matchesSearch && matchesRole;
    },
    [filterRole]
  );

  const { filtered, search, setSearch } = useFilteredList(visibleUsers, userFilter);

  const roleCounts = roles
    .filter((r) => visibleUsers.some((u) => u.role === r.name))
    .map((r) => ({ ...r, count: visibleUsers.filter((u) => u.role === r.name).length }));

  const hasActiveFilters = filterRole !== "all" || search !== "";
  const clearFilters = () => { setFilterRole("all"); setSearch(""); };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <h2 className="text-[28px] font-bold leading-[1.43] text-near-black">Users</h2>
              <span className="rounded-badge bg-near-black px-2.5 py-0.5 text-xs font-semibold tabular-nums text-white">{visibleUsers.length}</span>
            </div>
            <div className="flex flex-1 items-center gap-2 sm:max-w-md">
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
                <input className="w-full rounded-btn border border-border-card bg-white py-2 pl-10 pr-3 text-sm text-near-black placeholder:text-text-secondary focus:outline-none focus:shadow-focus" placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={["flex shrink-0 items-center gap-1.5 rounded-btn px-4 py-2 text-sm font-medium transition-colors", filtersOpen || hasActiveFilters ? "bg-near-black text-white" : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white"].join(" ")}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
                </svg>
                Filter
                {hasActiveFilters && (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-rausch px-1 text-[9px] font-bold text-white">
                    {(filterRole !== "all" ? 1 : 0) + (search !== "" ? 1 : 0)}
                  </span>
                )}
              </button>
              {currentUser.permissions.includes("users:write") && (
                <button onClick={() => setShowAdd(true)} className="shrink-0 rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch">
                  + Add user
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {filtersOpen && (
        <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <div className="rounded-card bg-white p-4 shadow-card">
              <div className="flex items-center justify-between">
                <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary">Filter by role</div>
                {hasActiveFilters && <button onClick={clearFilters} className="text-xs font-medium text-rausch hover:underline">Clear all</button>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => setFilterRole("all")} className={["rounded-btn px-3 py-1.5 text-xs font-medium transition-colors", filterRole === "all" ? "bg-near-black text-white" : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white"].join(" ")}>
                  All ({visibleUsers.length})
                </button>
                {roleCounts.map((r) => (
                  <button key={r.name} onClick={() => setFilterRole(r.name)} className={["rounded-btn px-3 py-1.5 text-xs font-medium transition-colors", filterRole === r.name ? "bg-near-black text-white" : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white"].join(" ")}>
                    {r.label} ({r.count})
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-6">
          {loading ? (
            <p className="text-text-secondary">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-text-secondary">No users found.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map((u) => (
                <UserCard key={u.id} user={u} currentUser={currentUser} assignable={assignable} onRefresh={refresh} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showAdd && (
        <AddUserDialog assignable={assignable} onClose={() => setShowAdd(false)} onCreated={refresh} isSuperAdmin={isSuperAdmin(currentUser)} />
      )}
    </div>
  );
}
